/**
 * feat-241 — one (crew, track) state cell. Four states, and NONE of them is
 * expressed by colour alone (AC16).
 *
 * Every cell carries TEXT. Colour is the second channel, glyph the third:
 *
 *   ✓ Done   |   3 of 7 + bar   |   Open   |   🔒 Locked / Needs Barista
 *
 * WHY `locked` HAS NO FILL. Four states on a 15-column grid need four
 * pre-attentively separable cells, and there are only two credible neutrals
 * (`gray-100` cool, `cream.dark` warm) 3% apart in lightness — a distinction
 * nobody can see across a scrolling table. So `locked` is separated by FORM, not
 * hue: no fill, a dashed hairline, a padlock. It is therefore the lightest cell
 * in the matrix, which is the correct hierarchy: `locked` is the one state a
 * manager can do nothing about.
 *
 * THE DIM COMES FROM REMOVING THE FILL, NEVER FROM FADING THE INK. `locked` ink
 * is `inkMuted` at FULL opacity (5.254:1 on white, 4.956:1 on the zebra row). An
 * `opacity-50` or `text-inkMuted/40` locked cell is the failure mode this
 * paragraph exists to prevent — measured, `inkMuted` at 60% composites to 2.4:1
 * on all three row surfaces.
 *
 * Design authority: `design/state-cells.md` §1, §4, §6.
 */
import React from 'react';
import { Check, Lock } from 'lucide-react';
import {
  CELL_BLOCKED_BY,
  CELL_BLOCKED_BY_FULL,
  CELL_FRACTION,
  CELL_LABEL,
  CELL_NOT_ASSIGNED,
  CELL_NOT_ASSIGNED_MARK,
  crewFirstName,
  IN_PROGRESS_INK,
  type CellStatus,
} from '../../../constants/curriculumCoverage';
import type { CoverageCell, CoverageTrack } from '../../../services/curriculumCoverageService';
import { cellApplies, cellLessonsTotal } from './coverageApplies';
import { resolveBlockerTitles } from './coverageBlockers';

/**
 * Complete, STATIC class strings per branch — never interpolated.
 *
 * `StatusChip.tsx` states the rule and this feature inherits it verbatim:
 * Tailwind purges classes it cannot see as whole tokens in the source, and a
 * purged class fails SILENTLY as an unstyled render. So this is a map of finished
 * strings, not `` `bg-${tone}-light` ``.
 *
 * `bg-gray-100 text-inkMuted` is not a raw-utility violation here — it is the
 * established neutral pill of this exact vocabulary (what the curriculum panes
 * already ship for `Hidden`), and the pair feat-240 measured at 4.774:1
 * precisely because it is that pill's fill.
 *
 * The `in_progress` entry reads its two classes from IN_PROGRESS_INK — the ONE
 * named reference for that ink (FRONTEND-SLICE §4 RULING 2). Both halves are
 * still whole tokens in the source that Tailwind can see, because they are
 * literals in the constants module.
 */
const CELL: Record<CellStatus, { tone: string; Icon: React.ElementType | null }> = {
  completed: { tone: 'bg-success-light text-success-on', Icon: Check },
  in_progress: { tone: `${IN_PROGRESS_INK.chipFill} ${IN_PROGRESS_INK.text}`, Icon: null },
  available: { tone: 'bg-gray-100 text-inkMuted', Icon: null },
  locked: { tone: 'text-inkMuted border border-dashed border-inkMuted/30', Icon: Lock },
};

export interface CoverageStateCellProps {
  cell: CoverageCell;
  /** The column's own track — the source of `lessons_total` (P14). */
  track: CoverageTrack;
  /** The full column list, for naming blockers by their column title. */
  tracks: readonly CoverageTrack[];
  /** feat-302 — the crew member's name, for "Not assigned to <first name>". */
  crewName?: string;
  /** feat-302 — tracks off-curriculum for this crew member; never named as blockers. */
  notApplicable?: ReadonlySet<string>;
}

export const CoverageStateCell: React.FC<CoverageStateCellProps> = ({
  cell,
  track,
  tracks,
  crewName = '',
  notApplicable,
}) => {
  // feat-302: off-curriculum for this person. A muted dash — no state colour,
  // no fill, no progress — whatever `status` the backend put beside it.
  if (!cellApplies(cell)) {
    const label = CELL_NOT_ASSIGNED(crewFirstName(crewName));
    return (
      <div
        className="rounded-lg px-2 py-2 text-center text-[11px] font-bold text-inkMuted"
        title={label}
        data-testid="coverage-cell-not_assigned"
      >
        <span aria-hidden>{CELL_NOT_ASSIGNED_MARK}</span>
        <span className="sr-only">{label}</span>
      </div>
    );
  }

  const { tone, Icon } = CELL[cell.status] ?? CELL.available;

  if (cell.status === 'in_progress') {
    const total = cellLessonsTotal(cell, track);
    const pct = total > 0 ? Math.min(100, Math.round((cell.lessons_complete / total) * 100)) : 0;
    return (
      <div className={`rounded-lg px-2 py-2 ${tone}`} data-testid="coverage-cell-in_progress">
        <div className="text-center text-[11px] font-bold">
          {/* The visible label IS the fraction (P7). This sr-only name exists so a
              screen-reader user does not hear a bare number in a grid of bare
              numbers. It is also why the scroll container must be `relative` —
              `.sr-only` is position:absolute and escapes an unpositioned
              overflow ancestor, widening the PAGE (design §8.4). */}
          <span className="sr-only">{CELL_LABEL.in_progress} — </span>
          {CELL_FRACTION(cell.lessons_complete, total)}
        </div>
        <div
          className={`mx-auto mt-1.5 h-[3px] w-full overflow-hidden rounded-full ${IN_PROGRESS_INK.barTrack}`}
          role="presentation"
        >
          {/* Graphic, 3:1 floor — and it clears 5.274:1 anyway. gold.DEFAULT on
              gold.light would be 1.932:1: a failing value that ships is one
              someone copies onto a surface where it is NOT redundant. */}
          <div className={`h-full rounded-full ${IN_PROGRESS_INK.barFill}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  }

  if (cell.status === 'locked') {
    const titles = resolveBlockerTitles(cell.blocking_track_ids, tracks, notApplicable);
    // A blocking id with no matching track must not render a blank second line.
    // Fall back to a bare "Locked" rather than printing a dangling "Needs ".
    const label = titles.length > 0 ? CELL_BLOCKED_BY(titles[0], titles.length - 1) : null;
    return (
      <div
        className={`rounded-lg px-2 py-1.5 text-center ${tone}`}
        // NATIVE title, not a popover: `overflow-x: auto` computes `overflow-y`
        // to `auto` as well, so any in-flow popover inside the scroll container
        // is clipped on BOTH axes. TrainingProgressPage keeps its column-header
        // tooltips native for exactly this reason. The passport shows the
        // complete list unclipped.
        title={titles.length > 0 ? CELL_BLOCKED_BY_FULL(titles) : undefined}
        data-testid="coverage-cell-locked"
      >
        <div className="flex items-center justify-center gap-1 text-[11px] font-bold">
          {Icon ? <Icon aria-hidden size={10} strokeWidth={3} /> : null}
          {CELL_LABEL.locked}
        </div>
        {label ? <div className="mt-0.5 truncate text-[10px]">{label}</div> : null}
      </div>
    );
  }

  // `completed` and `available`. The check glyph is DECORATIVE (aria-hidden) —
  // it duplicates the adjacent word "Done", and a glyph that repeats its
  // neighbouring text must not also be announced.
  return (
    <div
      className={`flex items-center justify-center gap-1 rounded-lg px-2 py-2 text-center text-[11px] font-bold ${tone}`}
      data-testid={`coverage-cell-${cell.status}`}
    >
      {Icon ? <Icon aria-hidden size={12} strokeWidth={3.5} /> : null}
      {CELL_LABEL[cell.status]}
    </div>
  );
};

export default CoverageStateCell;
